import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ACCOUNT_KEY } from '@/lib/account';
import { toAuthError } from '@/lib/auth';

import { useRefreshSession } from '../session';

/**
 * One write of Settings (`settings-is-a-dialog` D11, D12): the call is awaited, its result
 * is what decides, and only then do the two caches settle — the dialog's `['account']` and
 * the shell's `['session']`, so the nav follows a change made here.
 *
 * Nothing it returns closes the dialog, and nothing reports before the write landed. That
 * is the whole of the correction for Flutter's no-op `onSettingsUpdated`, its message
 * emitted beside the write and its `Completer` that nothing completes.
 */
export function useAccountWrite<Input, Output>(
  write: (input: Input) => Promise<Output>,
) {
  const client = useQueryClient();
  const refreshSession = useRefreshSession();
  return useMutation({
    mutationFn: write,
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ACCOUNT_KEY });
      refreshSession();
    },
  });
}

/** What a refused call says, which is the Worker's own sentence wherever it chose one. */
export function reasonFor(error: unknown): string {
  return toAuthError(error).message;
}
